import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Spinner,
  Tab,
  Tabs,
  ListGroup
} from 'react-bootstrap';
import axios from './utils/axiosConfig';

const ActivityNotifications = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [notificationSettings, setNotificationSettings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [targetCounts, setTargetCounts] = useState({}); // 儲存各設定的目標人數

  // 計算距離活動開始的剩餘時間
  const getTimeRemaining = (activityDate) => {
    const now = new Date();
    const eventDate = new Date(activityDate);
    const diffMs = eventDate - now;

    if (diffMs < 0) {
      return <Badge bg="secondary">已開始</Badge>;
    }

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;

    if (diffDays > 0) {
      return (
        <Badge bg="info">
          還有 {diffDays} 天 {remainingHours} 小時
        </Badge>
      );
    } else if (diffHours > 0) {
      return (
        <Badge bg="warning">
          還有 {diffHours} 小時
        </Badge>
      );
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return (
        <Badge bg="danger">
          還有 {diffMinutes} 分鐘
        </Badge>
      );
    }
  };

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [formData, setFormData] = useState({
    notify_hours_before: 24,
    notification_message: '',
    is_enabled: true,
    recipient_type: 'registered',
    include_family: false,
    test_mode: false  // 預設為正式模式，已報名者直接收到提醒
  });

  // Test modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testActivityId, setTestActivityId] = useState(null);

  // Add new notification modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedNewActivity, setSelectedNewActivity] = useState(null);

  // Log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [selectedActivityForLogs, setSelectedActivityForLogs] = useState(null);

  const token = localStorage.getItem('token');

  // 載入所有活動的通知設定列表
  const loadNotificationSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/activity-notifications');
      if (response.data.success) {
        setNotificationSettings(response.data.data);
        // 載入完設定後，獲取每個設定的目標人數
        loadTargetCounts(response.data.data);
      }
    } catch (err) {
      console.error('載入通知設定錯誤:', err);
      setError('載入通知設定失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 載入各設定的目標人數
  const loadTargetCounts = async (settings) => {
    const counts = {};
    const token = localStorage.getItem('token');

    for (const setting of settings) {
      try {
        const response = await axios.get(
          `/activity-notifications/${setting.activity_id}/target-count`,
          {
            params: {
              recipient_type: setting.recipient_type || 'registered',
              include_family: setting.include_family || 0
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (response.data.success) {
          counts[setting.id] = response.data.count;
        }
      } catch (err) {
        console.error(`載入活動 ${setting.activity_id} 目標人數錯誤:`, err);
        counts[setting.id] = 0;
      }
    }

    setTargetCounts(counts);
  };

  // 載入特定活動的通知記錄
  const loadActivityLogs = async (activityId) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `/activity-notifications/${activityId}/logs`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setActivityLogs(response.data.data);
        setSelectedActivityForLogs(activityId);
      }
    } catch (err) {
      console.error('載入通知記錄錯誤:', err);
      setError('載入通知記錄失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 載入未來的活動列表
  const loadUpcomingActivities = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔍 開始載入未來活動列表...');
      const response = await axios.get('/activity-notifications/upcoming-activities');
      console.log('📡 API 回應:', response);
      console.log('📊 回應資料:', response.data);

      if (response.data.success) {
        console.log('✅ 成功載入活動數量:', response.data.data?.length || 0);
        setActivities(response.data.data || []);
      } else {
        console.warn('⚠️ API 回應 success=false');
      }
    } catch (err) {
      console.error('❌ 載入活動列表錯誤:', err);
      console.error('錯誤詳情:', err.response);
      setError('載入活動列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotificationSettings();
    loadUpcomingActivities();
  }, []);

  // 開啟編輯 modal
  const handleEditSetting = async (setting) => {
    setCurrentActivity(setting);
    setFormData({
      notify_hours_before: setting.notify_hours_before || 24,
      notification_message: setting.notification_message || '',
      is_enabled: setting.is_enabled === 1,
      recipient_type: setting.recipient_type || 'registered',
      include_family: setting.include_family === 1,
      test_mode: setting.test_mode !== undefined ? setting.test_mode === 1 : false
    });
    setShowModal(true);
  };

  // 開啟新增通知 modal
  const handleAddNewNotification = (activity) => {
    setSelectedNewActivity(activity);
    setFormData({
      notify_hours_before: 24,
      notification_message: '',
      is_enabled: true,
      recipient_type: 'registered',
      include_family: false
    });
    setShowAddModal(true);
  };

  // 儲存新增的通知設定
  const handleSaveNewNotification = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(
        `/activity-notifications/${selectedNewActivity.a_id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setSuccess('通知設定已建立');
        setShowAddModal(false);
        loadNotificationSettings();
        loadUpcomingActivities();
      }
    } catch (err) {
      console.error('保存通知設定錯誤:', err);
      setError('保存設定失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 儲存通知設定
  const handleSaveSetting = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(
        `/activity-notifications/${currentActivity.activity_id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setSuccess('通知設定已保存');
        setShowModal(false);
        loadNotificationSettings();
      }
    } catch (err) {
      console.error('保存通知設定錯誤:', err);
      setError('保存設定失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 刪除通知設定
  const handleDeleteSetting = async (activityId) => {
    if (!window.confirm('確定要刪除此活動的通知設定嗎？')) return;

    setError('');
    setSuccess('');
    try {
      const response = await axios.delete(
        `/activity-notifications/${activityId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setSuccess('通知設定已刪除');
        loadNotificationSettings();
      }
    } catch (err) {
      console.error('刪除通知設定錯誤:', err);
      setError('刪除設定失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 測試發送通知
  const handleTestNotification = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(
        `/activity-notifications/${testActivityId}/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setSuccess('測試通知已發送到您的 LINE 帳號');
        setShowTestModal(false);
      }
    } catch (err) {
      console.error('測試通知錯誤:', err);
      setError('測試發送失敗：' + (err.response?.data?.error || err.message));
    }
  };

  // 手動觸發通知檢查
  const handleManualTrigger = async () => {
    if (!window.confirm('確定要立即執行通知檢查嗎？這會發送所有符合條件的活動通知。')) return;

    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const response = await axios.post(
        '/activity-notifications/trigger/now',
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setSuccess('通知檢查已執行：' + JSON.stringify(response.data.result));
      }
    } catch (err) {
      console.error('手動觸發錯誤:', err);
      setError('觸發失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">🔔 活動通知管理</h2>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        {/* 新增通知 Tab */}
        <Tab eventKey="add" title="➕ 新增通知">
          <Card>
            <Card.Header>選擇活動設定通知</Card.Header>
            <Card.Body>
              {loading && <Spinner animation="border" />}
              {!loading && activities.length === 0 && (
                <Alert variant="info">
                  目前沒有未來的活動可以設定通知
                  <br />
                  <small>提示：請確認活動管理中有已建立且開始日期在未來的活動</small>
                </Alert>
              )}
              {!loading && activities.length > 0 && (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>社別</th>
                      <th>活動名稱</th>
                      <th>活動日期</th>
                      <th>地點</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity) => {
                      // 使用後端返回的 has_notification 欄位
                      const hasNotification = activity.has_notification > 0;

                      return (
                        <tr key={activity.a_id}>
                          <td>{activity.ss_name || '-'}</td>
                          <td>#{activity.a_id} {activity.a_name}</td>
                          <td>
                            <div>
                              {new Date(activity.a_Sdate).toLocaleDateString('zh-TW', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              })}
                              {' '}
                              {new Date(activity.a_Sdate).toLocaleTimeString('zh-TW', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })}
                            </div>
                            <div className="mt-1">
                              {getTimeRemaining(activity.a_Sdate)}
                            </div>
                          </td>
                          <td>{activity.a_location || '-'}</td>
                          <td>
                            {hasNotification ? (
                              <Badge bg="success">已設定通知</Badge>
                            ) : (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleAddNewNotification(activity)}
                              >
                                設定通知
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* 通知設定 Tab */}
        <Tab eventKey="settings" title="通知設定">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>已設定的活動通知</span>
              <Button
                variant="primary"
                size="sm"
                onClick={handleManualTrigger}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : '手動觸發檢查'}
              </Button>
            </Card.Header>
            <Card.Body>
              {loading && <Spinner animation="border" />}
              {!loading && notificationSettings.length === 0 && (
                <Alert variant="info">目前沒有設定任何活動通知</Alert>
              )}
              {!loading && notificationSettings.length > 0 && (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>社別</th>
                      <th>活動名稱</th>
                      <th>活動時間</th>
                      <th>提前通知</th>
                      <th>發送對象</th>
                      <th>目標人數</th>
                      <th>狀態</th>
                      <th>已發送次數</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notificationSettings.map((setting) => {
                      const recipientTypeNames = {
                        'registered': '已報名',
                        'not_registered': '未報名',
                        'all_members': '全部社友'
                      };
                      const recipientTypeName = recipientTypeNames[setting.recipient_type] || '已報名';

                      return (
                      <tr key={setting.id}>
                        <td>{setting.ss_name || '-'}</td>
                        <td>#{setting.activity_id} {setting.a_name}</td>
                        <td>
                          <div>
                            {new Date(setting.a_Sdate).toLocaleDateString('zh-TW', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })}
                            {' '}
                            {new Date(setting.a_Sdate).toLocaleTimeString('zh-TW', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}
                          </div>
                          <div className="mt-1">
                            {getTimeRemaining(setting.a_Sdate)}
                          </div>
                        </td>
                        <td>{setting.notify_hours_before} 小時</td>
                        <td>
                          <div>{recipientTypeName}</div>
                          {setting.include_family === 1 && (
                            <Badge bg="info" className="mt-1">含家人</Badge>
                          )}
                        </td>
                        <td>
                          {targetCounts[setting.id] !== undefined ? (
                            <Badge bg="primary">{targetCounts[setting.id]} 人</Badge>
                          ) : (
                            <Spinner animation="border" size="sm" />
                          )}
                        </td>
                        <td>
                          {setting.is_enabled === 1 ? (
                            <Badge bg="success">啟用</Badge>
                          ) : (
                            <Badge bg="secondary">停用</Badge>
                          )}
                        </td>
                        <td>{setting.sent_count || 0}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditSetting(setting)}
                          >
                            編輯
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              setTestActivityId(setting.activity_id);
                              setShowTestModal(true);
                            }}
                          >
                            測試
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              loadActivityLogs(setting.activity_id);
                              setActiveTab('logs');
                            }}
                          >
                            記錄
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteSetting(setting.activity_id)}
                          >
                            刪除
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* 發送記錄 Tab */}
        <Tab eventKey="logs" title="發送記錄">
          <Card>
            <Card.Header>通知發送記錄</Card.Header>
            <Card.Body>
              {loading && <Spinner animation="border" />}
              {selectedActivityForLogs && activityLogs.length === 0 && (
                <Alert variant="info">此活動還沒有發送記錄</Alert>
              )}
              {!selectedActivityForLogs && (
                <Alert variant="info">請從通知設定頁面選擇活動查看記錄</Alert>
              )}
              {activityLogs.length > 0 && (
                <ListGroup>
                  {activityLogs.map((log) => (
                    <ListGroup.Item key={log.id}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>發送時間：</strong>
                          {new Date(log.notify_time).toLocaleString('zh-TW')}
                        </div>
                        <Badge bg={log.status === 'completed' ? 'success' : 'danger'}>
                          {log.status}
                        </Badge>
                      </div>
                      <div className="mt-2">
                        <span className="me-3">
                          <strong>總人數：</strong>{log.total_participants}
                        </span>
                        <span className="me-3 text-success">
                          <strong>成功：</strong>{log.notified_count}
                        </span>
                        <span className="text-danger">
                          <strong>失敗：</strong>{log.failed_count}
                        </span>
                      </div>
                      {log.error_message && (
                        <div className="mt-2 text-danger">
                          <strong>錯誤訊息：</strong>{log.error_message}
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* 使用說明 Tab */}
        <Tab eventKey="help" title="使用說明">
          <Card>
            <Card.Header>活動通知功能說明</Card.Header>
            <Card.Body>
              <h5>📋 功能概述</h5>
              <p>活動通知系統會在活動開始前的指定時間，自動向已報名的參加者發送 LINE 通知。</p>

              <h5>⚙️ 設定步驟</h5>
              <ol>
                <li>在「通知設定」頁面查看已有通知設定的活動</li>
                <li>點擊「編輯」按鈕可以修改通知設定：
                  <ul>
                    <li><strong>提前通知時間：</strong>活動開始前幾小時發送通知</li>
                    <li><strong>自訂訊息：</strong>可自訂通知內容（留空則使用預設訊息）</li>
                    <li><strong>啟用/停用：</strong>控制是否發送通知</li>
                  </ul>
                </li>
                <li>點擊「測試」可發送測試通知到您的 LINE 帳號</li>
                <li>點擊「記錄」查看該活動的通知發送歷史</li>
              </ol>

              <h5>🔔 自動排程</h5>
              <p>系統每小時自動檢查一次，符合以下條件的活動會自動發送通知：</p>
              <ul>
                <li>通知設定為「啟用」狀態</li>
                <li>距離活動開始時間 ≤ 設定的提前通知時間</li>
                <li>活動尚未開始</li>
                <li>今天還沒有發送過通知</li>
              </ul>

              <h5>📱 接收通知的條件</h5>
              <p>參加者需要符合以下條件才會收到通知：</p>
              <ul>
                <li>已報名該活動（employee_cardlog 有記錄）</li>
                <li>員工狀態為啟用（e_status = 0）</li>
                <li>已綁定 LINE ID（e_line_id 不為空）</li>
                <li>報名狀態為正常或已核准（j_status IN (0, 1)）</li>
              </ul>

              <h5>💡 自訂訊息變數</h5>
              <p>在自訂訊息中可以使用以下變數：</p>
              <ul>
                <li><code>{'{activity_name}'}</code> - 活動名稱</li>
                <li><code>{'{date}'}</code> - 活動日期</li>
                <li><code>{'{society}'}</code> - 社別名稱</li>
              </ul>

              <h5>🔧 管理員功能</h5>
              <p>管理員可以使用「手動觸發檢查」按鈕立即執行一次通知檢查，不需等待排程時間。</p>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* 新增通知設定 Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>新增活動通知設定</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedNewActivity && (
            <>
              <Alert variant="info">
                <strong>社別：</strong>{selectedNewActivity.ss_name || '-'}<br />
                <strong>活動：</strong>#{selectedNewActivity.a_id} {selectedNewActivity.a_name}<br />
                <strong>時間：</strong>
                {new Date(selectedNewActivity.a_Sdate).toLocaleDateString('zh-TW', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}
                {' '}
                {new Date(selectedNewActivity.a_Sdate).toLocaleTimeString('zh-TW', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
                <br />
                <strong>地點：</strong>{selectedNewActivity.a_location || '-'}
              </Alert>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>提前通知時間（小時）</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.notify_hours_before}
                    onChange={(e) => setFormData({ ...formData, notify_hours_before: parseInt(e.target.value) })}
                  />
                  <Form.Text className="text-muted">
                    活動開始前多久發送通知
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>發送對象</Form.Label>
                  <Form.Select
                    value={formData.recipient_type}
                    onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                  >
                    <option value="registered">已報名社友</option>
                    <option value="not_registered">未報名社友（同社別）</option>
                    <option value="all_members">全部社友（同社別）</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    選擇要發送通知的對象範圍
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="包含家人"
                    checked={formData.include_family}
                    onChange={(e) => setFormData({ ...formData, include_family: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    同時發送通知給參與社友的家人
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>自訂通知訊息（選填）</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={formData.notification_message}
                    onChange={(e) => setFormData({ ...formData, notification_message: e.target.value })}
                    placeholder="留空則使用預設訊息&#10;&#10;可用變數：&#10;{activity_name} - 活動名稱&#10;{date} - 活動日期&#10;{society} - 社別名稱"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="test-mode-switch-add"
                    label={formData.test_mode ? "🧪 測試模式（只發給管理員）" : "✅ 正式模式（發給所有目標對象）"}
                    checked={formData.test_mode}
                    onChange={(e) => setFormData({ ...formData, test_mode: e.target.checked })}
                  />
                  <Form.Text className={formData.test_mode ? "text-warning" : "text-success"}>
                    {formData.test_mode
                      ? "測試模式下，通知只會發送給管理員（用於測試驗證）"
                      : "⚠️ 正式模式下，通知會發送給所有符合條件的社友"}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="啟用通知"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveNewNotification}>
            建立通知設定
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 編輯通知設定 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>編輯通知設定</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentActivity && (
            <>
              <Alert variant="info">
                <strong>活動：</strong>#{currentActivity.activity_id} {currentActivity.a_name}<br />
                <strong>時間：</strong>
                {new Date(currentActivity.a_Sdate).toLocaleDateString('zh-TW', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}
                {' '}
                {new Date(currentActivity.a_Sdate).toLocaleTimeString('zh-TW', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })}
              </Alert>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>提前通知時間（小時）</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.notify_hours_before}
                    onChange={(e) => setFormData({ ...formData, notify_hours_before: parseInt(e.target.value) })}
                  />
                  <Form.Text className="text-muted">
                    活動開始前多久發送通知
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>發送對象</Form.Label>
                  <Form.Select
                    value={formData.recipient_type}
                    onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                  >
                    <option value="registered">已報名社友</option>
                    <option value="not_registered">未報名社友（同社別）</option>
                    <option value="all_members">全部社友（同社別）</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    選擇要發送通知的對象範圍
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="包含家人"
                    checked={formData.include_family}
                    onChange={(e) => setFormData({ ...formData, include_family: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    同時發送通知給參與社友的家人
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>自訂通知訊息（選填）</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={formData.notification_message}
                    onChange={(e) => setFormData({ ...formData, notification_message: e.target.value })}
                    placeholder="留空則使用預設訊息&#10;&#10;可用變數：&#10;{activity_name} - 活動名稱&#10;{date} - 活動日期&#10;{society} - 社別名稱"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="test-mode-switch-edit"
                    label={formData.test_mode ? "🧪 測試模式（只發給管理員）" : "✅ 正式模式（發給所有目標對象）"}
                    checked={formData.test_mode}
                    onChange={(e) => setFormData({ ...formData, test_mode: e.target.checked })}
                  />
                  <Form.Text className={formData.test_mode ? "text-warning" : "text-success"}>
                    {formData.test_mode
                      ? "測試模式下，通知只會發送給管理員（用於測試驗證）"
                      : "⚠️ 正式模式下，通知會發送給所有符合條件的社友"}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="啟用通知"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveSetting}>
            保存設定
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 測試通知 Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>測試通知</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            測試通知將發送到您綁定的 LINE 帳號。<br />
            請確認您已綁定 LINE ID。
          </Alert>
          <p>確定要發送測試通知嗎？</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleTestNotification}>
            發送測試
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ActivityNotifications;
